import { forwardRef } from 'react';
import type { SyntheticEvent } from 'react';
import { imgSrcSet, imgW } from '../lib/img';

export type ProductImageVariant = 'card' | 'gallery' | 'full' | 'thumbnail' | 'cart';

export interface ProductImageProps {
  url: string | null | undefined;
  alt: string;
  /** Whether this image has a transparent background and should float. */
  isTransparent: boolean;
  /** Which display context the image is shown in. */
  variant?: ProductImageVariant;
  /** srcSet widths. Defaults depend on the variant. */
  widths?: number[];
  /** Sizes attribute for the srcSet. */
  sizes?: string;
  loading?: 'eager' | 'lazy';
  fetchPriority?: 'high' | 'low' | 'auto';
  onLoad?: (e: SyntheticEvent<HTMLImageElement>) => void;
  onError?: () => void;
  /** Extra classes appended after the variant classes. */
  className?: string;
}

const DEFAULT_WIDTHS: Record<ProductImageVariant, number[]> = {
  card: [200, 400, 800, 1200],
  gallery: [400, 800, 1200],
  full: [],
  thumbnail: [100, 200],
  cart: [100, 200],
};

const DEFAULT_SIZES: Record<ProductImageVariant, string | undefined> = {
  card: '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw',
  gallery: '(max-width: 1024px) 90vw, 45vw',
  full: undefined,
  thumbnail: '64px',
  cart: '(max-width: 640px) 64px, 80px',
};

const TRANSPARENT_CLASSES: Record<ProductImageVariant, string> = {
  card: 'block max-h-full max-w-full object-contain object-center drop-shadow-float',
  gallery: 'mx-auto block max-h-full max-w-[90%] object-contain object-center drop-shadow-float-lg',
  full: 'max-h-[78vh] w-auto object-contain object-center drop-shadow-float-xl',
  thumbnail: 'block h-full w-full object-contain object-center drop-shadow-float',
  cart: 'block h-full w-full object-contain object-center drop-shadow-float',
};

const SOLID_CLASSES: Record<ProductImageVariant, string> = {
  card: 'h-full w-full object-cover',
  gallery: 'h-full w-full object-cover',
  full: 'max-h-[78vh] w-auto object-contain rounded-lg shadow-soft',
  thumbnail: 'h-full w-full object-cover',
  cart: 'h-full w-full object-cover',
};

export const ProductImage = forwardRef<HTMLImageElement, ProductImageProps>(function ProductImage(
  {
    url,
    alt,
    isTransparent,
    variant = 'card',
    widths,
    sizes,
    loading = 'lazy',
    fetchPriority,
    onLoad,
    onError,
    className = '',
  },
  ref,
) {
  const src = url ? imgW(url, variant === 'full' ? 99999 : (widths?.[0] ?? DEFAULT_WIDTHS[variant][0] ?? 400)) : undefined;
  const srcSet = url?.startsWith('/uploads/')
    ? imgSrcSet(url, widths ?? DEFAULT_WIDTHS[variant])
    : undefined;
  const resolvedSizes = sizes ?? DEFAULT_SIZES[variant];

  return (
    <img
      ref={ref}
      src={src}
      srcSet={srcSet}
      sizes={resolvedSizes}
      alt={alt}
      loading={loading}
      decoding="async"
      fetchPriority={fetchPriority}
      onLoad={onLoad}
      onError={onError}
      className={`${isTransparent ? TRANSPARENT_CLASSES[variant] : SOLID_CLASSES[variant]} ${className}`}
    />
  );
});
